import { inSubGroup, type NavigationEntry, type NavigationSubGroup } from "@positive/ui";
import "@ui5/webcomponents-icons/dist/add-product.js";
import "@ui5/webcomponents-icons/dist/inventory.js";
import "@ui5/webcomponents-icons/dist/product.js";
import "@ui5/webcomponents-icons/dist/shipping-status.js";
import "@ui5/webcomponents-icons/dist/journey-depart.js";
import "@ui5/webcomponents-icons/dist/workflow-tasks.js";
import "@ui5/webcomponents-icons/dist/tag.js";
import "@ui5/webcomponents-icons/dist/lead.js";
import "@ui5/webcomponents-icons/dist/clinical-tast-tracker.js";
import "@ui5/webcomponents-icons/dist/money-bills.js";
import "@ui5/webcomponents-icons/dist/table-view.js";
import "@ui5/webcomponents-icons/dist/settings.js";
import "@ui5/webcomponents-icons/dist/process.js";
import "@ui5/webcomponents-icons/dist/manager-insight.js";
import "@ui5/webcomponents-icons/dist/locked.js";

import { catalogPermissions } from "../catalog/permissions";
import {
  documentKinds,
  kindKey,
  kindPermission,
  listPaths,
  reportKeyName,
  reportKeys,
  reportPermission,
} from "./kinds";
import { invPermissions } from "./permissions";

const kindSubGroup: Partial<Record<string, NavigationSubGroup>> = {
  openingStock: "specialMovements",
  stockTypeChange: "specialMovements",
  revaluation: "specialMovements",
};

const capital = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

const documentIcons: Record<string, string> = {
  "opening-stock": "add-product",
  "goods-issues": "shipping-status",
  "goods-receipts": "product",
  adjustments: "inventory",
  transfers: "journey-depart",
  "stock-type-changes": "workflow-tasks",
  revaluations: "money-bills",
};

/** Inventory entries of the navigation registry (group `inventory`, plan module `inventory`). */
export const inventoryNavigation: readonly NavigationEntry[] = [
  ...documentKinds.map((kind) => ({
    key: `inv.${kindKey[kind]}`,
    ...inSubGroup(kindSubGroup[kindKey[kind]]),
    labelKey: `nav.inv${capital(kindKey[kind])}`,
    icon: documentIcons[kind] ?? "inventory",
    path: listPaths[kind],
    permission: kindPermission(kind, "view"),
  })),
  {
    key: "inv.transferAcceptance",
    labelKey: "nav.invTransferAcceptance",
    icon: "lead",
    path: "/inv/transfers-awaiting",
    permission: invPermissions.acceptanceView,
  },
  {
    key: "inv.counts",
    labelKey: "nav.invCounts",
    icon: "clinical-tast-tracker",
    path: "/inv/counts",
    permission: invPermissions.countView,
  },
  {
    key: "inv.landedCosts",
    subGroup: "specialMovements",
    labelKey: "nav.invLandedCosts",
    icon: "money-bills",
    path: "/inv/landed-costs",
    permission: invPermissions.landedCostView,
  },
  {
    key: "inv.replenishment",
    labelKey: "nav.invReplenishment",
    icon: "process",
    path: "/inv/replenishment",
    permission: invPermissions.reorderView,
  },
  ...reportKeys.map((report) => ({
    key: `inv.report.${reportKeyName[report]}`,
    subGroup: "reports" as const,
    labelKey: `nav.invReport${capital(reportKeyName[report])}`,
    icon: "table-view",
    path: `/inv/reports/${report}`,
    permission: reportPermission[report].view,
  })),
  {
    key: "inv.reorderRules",
    subGroup: "setup",
    labelKey: "nav.invReorderRules",
    icon: "process",
    path: "/inv/reorder-rules",
    permission: invPermissions.reorderView,
  },
  {
    key: "inv.costing",
    subGroup: "setup",
    labelKey: "nav.invCosting",
    icon: "manager-insight",
    path: "/inv/costing",
    permission: invPermissions.costingView,
  },
  {
    key: "inv.reasons",
    subGroup: "setup",
    labelKey: "nav.invReasons",
    icon: "tag",
    path: "/inv/reason-codes",
    permission: invPermissions.reasonView,
  },
  {
    key: "inv.warehouseAccess",
    subGroup: "setup",
    labelKey: "nav.invWarehouseAccess",
    icon: "locked",
    path: "/inv/warehouse-access",
    permission: catalogPermissions.warehouseView,
  },
  {
    key: "inv.accountSettings",
    subGroup: "setup",
    labelKey: "nav.invAccountSettings",
    icon: "settings",
    path: "/inv/account-settings",
    permission: invPermissions.accountSettingsView,
  },
];
